import React from 'react';

import calendarUnavailability from './assets/faq/calendar-unavailability.png';
import editEducation from './assets/faq/edit-education.png';
import editUnavailability from './assets/faq/edit-unavailability.png';
import educationList from './assets/faq/education-list.png';
import guideVersionHistory from './assets/faq/guide-version-history.png';
import profileMenu from './assets/faq/profile-menu.png';
import removeEducation from './assets/faq/remove-education.png';
import removeUnavailability from './assets/faq/remove-unavailability.png';
import reviewGuideList from './assets/faq/review-guide-list.png';
import reviewToolsSummary from './assets/faq/review-tools-summary.png';

const zendeskRequestUrl =
  '/external-link/?url=https://ohs-itams.zendesk.com/hc/en-us/requests/new';

export const faqCategories = [
  {
    id: 'all-users',
    title: 'All Users',
    items: [
      {
        id: 'contact-help-desk',
        question: 'Who do I contact when I have an IT-AMS problem?',
        answer: (
          <>
            <p>
              Contact the IT-AMS Help Desk. The Zendesk Service Portal is the
              recommended support channel because you can submit a request and
              track ticket status and updates in one place.
            </p>
            <p>
              <a href={zendeskRequestUrl}>
                Submit an IT-AMS Help Desk request through the Zendesk Service
                Portal
              </a>
              .
            </p>
            <p>
              You can also email{' '}
              <a href="mailto:ITAMSHelpDesk@acf.hhs.gov">
                ITAMSHelpDesk@acf.hhs.gov
              </a>{' '}
              or call <a href="tel:+18669931295">(866) 993-1295</a>. Help Desk
              contact information is available in the footer of every IT-AMS
              page.
            </p>
          </>
        ),
      },
      {
        id: 'update-profile',
        question: 'How do I update my IT-AMS profile?',
        answer: (
          <>
            <p>
              Profile sections may include Contact Information, Demographic,
              Education, Experience, Miscellaneous, Travel Preference, Training,
              Additional Credentials, Change Password, Skills, and Review
              History. Not every section is editable because some information is
              generated by IT-AMS.
            </p>
            <img
              className="faq-screenshot faq-screenshot--compact"
              src={profileMenu}
              alt="Profile Menu with Contact Information selected and the available profile sections listed"
            />
            <p>To update an editable profile section:</p>
            <ol>
              <li>
                Open the user menu by selecting the downward arrow next to your
                username, then select Profile.
              </li>
              <li>Use the Profile Menu to open the section you need.</li>
              <li>Select the section you want to update.</li>
              <li>Select Edit when it is available.</li>
              <li>Make the permitted changes, then select Save.</li>
              <li>
                After a successful update, IT-AMS displays a confirmation
                message that the data was saved successfully.
              </li>
            </ol>
            <p>
              To add new Education, Additional Credentials, or Languages
              (located under the Demographic menu option):
            </p>
            <ol>
              <li>Select Edit.</li>
              <li>Select Add and enter the new information.</li>
              <li>Select Save.</li>
              <li>
                After a successful update, IT-AMS displays a confirmation
                message that the data was saved successfully.
              </li>
            </ol>
            <img
              className="faq-screenshot"
              src={editEducation}
              alt="Edit Certification dialog showing fields for degree name, issuer, date received, verification status, and the Save button"
            />
            <p>To edit an existing education, credential, or language entry:</p>
            <ol>
              <li>Select Edit and then select the pencil icon for the item.</li>
              <li>Make your changes in the edit window and select Save.</li>
              <li>
                After a successful update, IT-AMS displays a confirmation
                message that the data was saved successfully.
              </li>
            </ol>
            <img
              className="faq-screenshot"
              src={educationList}
              alt="Education profile section showing an existing degree and pencil and trash can action icons"
            />
            <p>
              To delete an existing education, credential, or language entry:
            </p>
            <ol>
              <li>Select Edit.</li>
              <li>
                Select the trash can icon to the right of the item you want to
                delete.
              </li>
              <li>Select Remove.</li>
              <li>
                After a successful update, IT-AMS displays a confirmation
                message that the data was saved successfully.
              </li>
            </ol>
            <img
              className="faq-screenshot"
              src={removeEducation}
              alt="Remove Certification or Degree confirmation dialog with a Remove button"
            />
          </>
        ),
      },
      {
        id: 'add-calendar-unavailability',
        question:
          'How do I add dates to my calendar when I am unavailable for review scheduling?',
        answer: (
          <ol>
            <li>Open Calendar.</li>
            <li>Select Add Unavailability.</li>
            <li>
              Enter the From and To dates for the period when you are
              unavailable.
            </li>
            <li>
              If any dates within that range should remain available, select
              them from the Exclude Dates (Optional) drop-down menu.
            </li>
            <li>Select Save Dates.</li>
            <li>
              Select Yes when IT-AMS asks you to confirm the unavailability.
            </li>
            <li>
              After a successful update, IT-AMS displays a confirmation message
              that the data was saved successfully.
            </li>
          </ol>
        ),
      },
      {
        id: 'remove-calendar-unavailability',
        question: 'How do I remove unavailability from my calendar?',
        answer: (
          <>
            <ol>
              <li>Open Calendar.</li>
              <li>
                Select the date range you want to delete on your calendar by
                clicking the red line.
              </li>
            </ol>
            <img
              className="faq-screenshot"
              src={calendarUnavailability}
              alt="My Calendar page showing an Unavailable date range as a labeled red bar"
            />
            <ol start="3">
              <li>Select Delete from the Unavailability window.</li>
            </ol>
            <img
              className="faq-screenshot faq-screenshot--compact"
              src={editUnavailability}
              alt="Editing Selected Unavailability dialog showing the date range and Delete button"
            />
            <ol start="4">
              <li>Select Yes, Remove it!</li>
            </ol>
            <img
              className="faq-screenshot faq-screenshot--compact"
              src={removeUnavailability}
              alt="Remove Unavailability confirmation dialog with No, cancel and Yes, Remove it buttons"
            />
            <ol start="5">
              <li>
                The system will not display a successfully deleted confirmation
                message. Your confirmation is that the red line for the deleted
                date range is no longer visible on your calendar.
              </li>
            </ol>
          </>
        ),
      },
      {
        id: 'access-assigned-review',
        question: 'How do I access my assigned review?',
        answer: (
          <>
            <p>
              Assigned reviews may be available from Tasks on the Home page,
              from your Calendar, or from the Reviews page.
            </p>
            <p>To locate a review directly:</p>
            <ol>
              <li>
                Open Reviews and use the available filters to narrow the Review
                List.
              </li>
              <li>
                Search using information such as Review ID, grantee, fiscal
                year, review type, or other available criteria.
              </li>
              <li>
                Review the results and select the correct Review ID to open the
                Review Detail page.
              </li>
              <li>
                Confirm that the review information matches your assignment
                before beginning work.
              </li>
            </ol>
          </>
        ),
      },
      {
        id: 'locate-review-guides',
        question: 'How do I locate the guides I need for a review?',
        answer: (
          <>
            <ol>
              <li>Open the assigned review.</li>
              <li>
                From the Review Details page, guides are located at the bottom
                of the page.
              </li>
            </ol>
            <img
              className="faq-screenshot"
              src={reviewGuideList}
              alt="Review Detail page showing the Guide filters and Guide List at the bottom of the page"
            />
            <ol start="3">
              <li>
                Use the Guide List to locate the guide associated with your
                work. Available guides may differ by review type, role,
                assignment, or review status.
              </li>
              <li>
                When available, you can also use the Guide filter to narrow the
                list by Guide Name or Status.
              </li>
              <li>
                Select the Guide Name to open the guide, then confirm that you
                are in the correct review and work area before entering
                information.
              </li>
            </ol>
            <p>To view an earlier saved version of a guide:</p>
            <ol>
              <li>Open the review.</li>
              <li>
                From the Review Detail page, select the guide you want to view.
              </li>
              <li>
                Scroll to the version history table at the bottom of the guide.
              </li>
              <li>
                Under Version, select the hyperlinked version number you want to
                open.
              </li>
              <li>
                Use the Edited By and Edited Time columns to confirm that you
                selected the intended historical version.
              </li>
            </ol>
            <img
              className="faq-screenshot"
              src={guideVersionHistory}
              alt="Guide page showing the version history table with Version, Edited By, and Edited Time columns"
            />
          </>
        ),
      },
      {
        id: 'summary-pages',
        question:
          'Where do I find the Pre-Review and Management Systems summary pages?',
        answer: (
          <>
            <ol>
              <li>
                Open the review, then use the Review Tools menu on the Review
                Detail page.
              </li>
              <li>
                When those summary pages are available for the review, select
                Pre-Review Summary or Management Systems Analysis Summary.
                Review Tool options vary by review type, so not every option
                appears in every review.
              </li>
            </ol>
            <img
              className="faq-screenshot"
              src={reviewToolsSummary}
              alt="Review page with the Review Tools menu beside the Follow-up Data Collection content"
            />
          </>
        ),
      },
      {
        id: 'simultaneous-review-updates',
        question:
          'What are the limitations for multiple people entering information into the same review?',
        answer: (
          <>
            <p>
              Avoid having multiple people actively update the same guide in the
              same review at the same time. Simultaneous updates can create
              conflicting changes and may cause one person’s work to overwrite
              another person’s work.
            </p>
            <p>
              Coordinate with the review team before making updates. Before
              handing work to another user, allow guide autosave to finish or
              complete any required Save action.
            </p>
          </>
        ),
      },
      {
        id: 'autosave',
        question:
          'Which parts of IT-AMS have autosave? Which parts require a manual save?',
        answer: (
          <>
            <p>
              Work completed within a guide is autosaved. Other IT-AMS functions
              require manual Save action.
            </p>
            <ul>
              <li>Attach Documents requires Save Attachment.</li>
              <li>Program Overview updates require Save.</li>
              <li>Profile updates require Save.</li>
              <li>Calendar unavailability updates require Save Dates.</li>
            </ul>
            <p>
              When IT-AMS displays a Save, Save Attachment, Save Dates, or other
              save action, complete that action before leaving the page.
            </p>
          </>
        ),
      },
    ],
  },
  {
    id: 'review-leadership',
    title: 'RTMs, MSLs, and RCLs',
    items: [
      {
        id: 'review-adjustment-notifications',
        question:
          'How am I notified when adjustments are needed from my manager, the reporting team, or OHS for my assigned review?',
        answer: (
          <>
            <p>
              IT-AMS uses assigned Tasks and system-generated email
              notifications as part of review workflows. When you receive a
              notification or a review is returned for additional work, use the
              Review ID to locate the review, review the requested action, and
              confirm the current review status before making changes.
            </p>
            <p>
              The current draft also references Journal comments and specific
              automatic status changes as ways adjustment requests may be
              communicated.
            </p>
            <aside className="faq-validation-note" aria-label="Validation note">
              <strong>SME validation needed before publication:</strong> Confirm
              the current notification source, expected sender, status changes,
              use of Journal comments, and whether returned work always appears
              in Tasks. The screenshots in this draft do not verify those
              behaviors.
            </aside>
          </>
        ),
      },
    ],
  },
  {
    id: 'general-rcs',
    title: 'General RCs',
    emptyMessage: 'No General RC FAQ questions.',
    items: [],
  },
];

export default faqCategories;
